import { createContext, useContext } from "react";
import type { EntityRefValue, IssueRow } from "#app/lib/data";
import type {
  CustomFieldDef,
  IssueTypeWithFields,
  IssueViewStage,
} from "#app/components/issue-view/issue-view-types";
import type { ActivityContextValue } from "#app/components/issue-view/activity/activity-context";
import type { NoteNode } from "#app/components/issue-view/activity/activity-types";
import type { IssueMediaItem } from "#app/hooks/issue-view/use-issue-media-items";
import type { Omittable } from "@salestrak/ui";

export type IssueWidgetNavigateTab = "activity" | "media";

/**
 * Everything the issue widgets read: the summary tab resolves issue data +
 * mutation callbacks once (create vs view mode) and provides them here, so the
 * widget bodies stay presentation-only. Callbacks arrive pre-gated — e.g.
 * `onAddPhoto` is already `undefined` for read-only viewers and create mode.
 */
export type IssueWidgetContextValue = Omittable<{
  issue?: IssueRow;
  stage?: IssueViewStage | null;
  workflowId?: string | null;
  address?: {
    id: string;
    addressId: string;
    wholeAddress?: string | null;
    latLng?: { lat: number; lng: number } | null;
  } | null;
  assignees?: EntityRefValue[];
  issueTypes?: IssueTypeWithFields[];
  onIssueTypeChange?: (issueTypeId: string) => void;
  values?: Record<string, unknown>;
  customFields?: CustomFieldDef[];
  orderedTypeFieldIds: string[];
  requiredFieldIds?: string[];
  /** Fields the type keeps on the summary while empty — a reading rule for
   *  everyone, never a validation one. */
  alwaysShownFieldIds?: string[];
  onUpdateValue?: (data: { customFieldId: string; value: unknown }) => void;
  onAddPhoto?: (customFieldId: string, photoId: string) => void | Promise<void>;
  onRemovePhoto?: (customFieldId: string, photoId: string) => void | Promise<void>;
  /** The description the Description widget renders: the draft's in create
   *  mode, the issue's in view mode, resolved once by the summary. */
  description?: string;
  /** Resolves when the write has landed, so the editor can say "Saved"
   *  after the fact rather than at the keystroke; a draft write is sync. */
  onUpdateDescription?: (description: string) => void | Promise<unknown>;
  onCloseIssue?: () => void;
  focusCustomFieldId?: string | null;
  isCreateMode: boolean;
  onNavigateTab?: (tab: IssueWidgetNavigateTab) => void;
  activity?: ActivityContextValue;
  activityLoading?: boolean;
  notes?: NoteNode[];
  /** Every media item on the issue (field photos, note attachments) —
   *  computed once by the issue body and shared with the media tab. */
  mediaItems?: IssueMediaItem[];
}>;

const IssueWidgetContext = createContext<IssueWidgetContextValue | null>(null);

export const IssueWidgetProvider = IssueWidgetContext.Provider;

export function useIssueWidgetContext(): IssueWidgetContextValue {
  const value = useContext(IssueWidgetContext);
  if (!value) {
    throw new Error("useIssueWidgetContext must be used within IssueWidgetProvider");
  }
  return value;
}
